import { isEmpty, some } from "lodash";
import { Activity } from "../../../@types/activities.ts";
import { Activitystream } from "../../../api/activitystreams.ts";
import { useCanWriteToAnyStream } from "../../../hooks/useCanWriteToAnyStream.ts";

export type ActivityShareOptions = {
  asPost: boolean;
  emailInternal: boolean;
  emailExternal: boolean;
  pdf: boolean;
  hasAny: boolean;
};

type ShareOptionsInput = {
  activity: Pick<Activity, "shared_from" | "can"> & {
    object: Pick<Activity["object"], "shareable">;
  };
  streamsFromActivity?: Activitystream[];
  canWriteToAnyStream: boolean;
  isPublicView: boolean;
  isApp: boolean;
};

// Which entries of the "Beitrag teilen" chooser are allowed for an activity.
// Pure so the permission matrix stays unit-testable; the hook below only
// feeds it the global state.
export function computeActivityShareOptions({
  activity,
  streamsFromActivity,
  canWriteToAnyStream,
  isPublicView,
  isApp,
}: ShareOptionsInput): ActivityShareOptions {
  const asPost = Boolean(
    activity.object.shareable &&
    isEmpty(activity.shared_from) &&
    activity.can?.share &&
    canWriteToAnyStream,
  );
  const emailInternal = some(
    streamsFromActivity,
    (stream) => stream.can?.email_recommend_activity_internally,
  );
  const emailExternal = some(
    streamsFromActivity,
    (stream) => stream.can?.email_recommend_activity_externally,
  );
  // The download is hidden in the native app for now (no bridge support yet)
  const pdf = !isApp && !isPublicView;

  return {
    asPost,
    emailInternal,
    emailExternal,
    pdf,
    hasAny: asPost || emailInternal || emailExternal || pdf,
  };
}

export function useActivityShareOptions({
  activity,
  streamsFromActivity,
  isPublicView,
}: {
  activity: ShareOptionsInput["activity"];
  streamsFromActivity?: Activitystream[];
  isPublicView: boolean;
}): ActivityShareOptions {
  const canWriteToAnyStream = useCanWriteToAnyStream();

  return computeActivityShareOptions({
    activity,
    streamsFromActivity,
    canWriteToAnyStream,
    isPublicView,
    isApp: Boolean(window.isApp),
  });
}
